import { ErrorMessage, Field, Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import networkService from "~/services/network.service";
import CustomSelect from "~/shared/components/customSelect";
import PageHeader from "~/shared/components/page-header.component";
import { GET_PLANS, SERVICE_ID } from "~/shared/constants/api";
import useAuthStore from "~/store/auth.store";
import {
  billingCycleValues,
  paymentTypeValues,
  unitValues,
  customBillingUnitValues
} from "./app-plan.helper";
import useSidebarStore from "~/store/sidebar.store";
import { handleError } from "~/shared/utils/errors.util";

export default function AddPlan() {
  const serviceId = SERVICE_ID;
  const navigate = useNavigate();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [collapsed] = useSidebarStore((state) => [state.collapsed]);

  const initialValues = {
    name: "",
    description: "",
    type: "",
    licenseeType: "DEVICE",
    pricingModel: {
      type: "PAY_PER_LICENSE",
      billingCycle: "",
      customBillingInterval: 1,
      customBillingUnit: "",
      unitPrice: 0,
      unitPriceOriginal: 0,
      discountPercent: 0,
      unit: "",
    },
    trialPeriod: 0,
  };

  const handleSubmit = async (values: any) => {
    if (!accessToken) {
      toast.error("Authorization token is missing!");
      return;
    }

    // Calculate discounted price if discountPercent is provided
    let unitPriceOriginal = Number(values.pricingModel.unitPriceOriginal) || Number(values.pricingModel.unitPrice) || 0;
    let discountPercent = Number(values.pricingModel.discountPercent) || 0;
    let unitPrice = unitPriceOriginal;
    if (discountPercent > 0 && unitPriceOriginal > 0) {
      unitPrice = +(unitPriceOriginal * (1 - discountPercent / 100)).toFixed(2);
    }

    // Prepare the base pricing model
    const pricingModel: any = {
      type: "PAY_PER_LICENSE",
      unitPrice,
      unitPriceOriginal: unitPriceOriginal,
      unit: values.pricingModel.unit,
    };

    // Handle billing cycle format based on selection
    if (values.pricingModel.billingCycle === "custom") {
      pricingModel.billingCycle = "CUSTOM";
      pricingModel.interval = values.pricingModel.customBillingUnit;
      pricingModel.intervalCount = Number(values.pricingModel.customBillingInterval);
    } else {
      pricingModel.billingCycle = values.pricingModel.billingCycle;
    }

    // Complete payload
    const payload = {
      name: values.name,
      description: values.description,
      type: values.type,
      licenseeType: values.licenseeType,
      trialPeriod: Number(values.trialPeriod),
      pricingModel
    };

    try {
      await networkService.post<any>(`${GET_PLANS}/${serviceId}/plans/create`, payload);
      toast.success("Plan Created Successfully!");
      navigate(-1);
    } catch (error: any) {
      handleError({ error: error, message: error.message, level: "error" });
    }
  };

  const planSchema = Yup.object({
    name: Yup.string().required("Plan name is required"),
    description: Yup.string().required("Description is required"),
    type: Yup.string()
      .oneOf(
        paymentTypeValues.map((type) => type.value),
        "Invalid plan type"
      )
      .required("Plan type is required"),
    licenseeType: Yup.string().required("Licensee type is required"),
    pricingModel: Yup.object({
      unitPrice: Yup.number()
        .min(0, "Price must be positive")
        .required("Price is required"),
      billingCycle: Yup.string()
        .oneOf(
          [...billingCycleValues.map((cycle) => cycle.value), "custom"],
          "Invalid billing cycle"
        )
        .required("Billing cycle is required"),
      customBillingInterval: Yup.number()
        .when("billingCycle", {
          is: "custom",
          then: (schema) => schema.min(1, "Interval must be at least 1").required("Interval is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      customBillingUnit: Yup.string()
        .when("billingCycle", {
          is: "custom",
          then: (schema) => schema.required("Unit is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      unit: Yup.string()
        .oneOf(
          unitValues.map((unit) => unit.value),
          "Invalid unit"
        )
        .required("Unit is required"),
    }),
    trialPeriod: Yup.number()
      .min(0, "Trial period must be positive")
      .optional(),
  });

  return (
    <div
      className={`flex flex-col flex-grow p-8 overflow-hidden ${collapsed ? "ml-20" : "ml-72"} transition-all duration-300`}>
      <PageHeader title="Plans" />
      <div className="sm:px-0 mt-4">
        <h3 className="text-normal font-semibold leading-7 text-Neutral-900 font-space-grotesk">
          Plans &gt; Add Plan
        </h3>
      </div>

      <div className="bg-white rounded-lg shadow-sm mt-6 p-8">
        <Formik
          initialValues={initialValues}
          validationSchema={planSchema}
          onSubmit={handleSubmit}>
          {({ isSubmitting, setFieldValue, values }) => (
            <Form>
              <div className="space-y-8">
                <div className="grid gap-x-8 gap-y-6 grid-cols-1 md:grid-cols-2">
                  <div className="space-y-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium leading-6 text-gray-900">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Field
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter plan name"
                        className="input px-4 py-2 w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent mt-1"
                      />
                      <ErrorMessage
                        name="name"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="type"
                        className="block text-sm font-medium leading-6 text-gray-900">
                        Plan Type <span className="text-red-500">*</span>
                      </label>
                      <CustomSelect
                        id="type"
                        name="type"
                        selectedValue={values.type}
                        onChangeHandler={(value) => {
                          setFieldValue("type", value);
                          if (value === "free") {
                            setFieldValue("pricingModel.unitPrice", 0);
                          }
                        }}>
                        {paymentTypeValues.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </CustomSelect>
                      <ErrorMessage
                        name="type"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="billingCycle"
                        className="block text-sm font-medium leading-6 text-gray-900">
                        Billing Cycle <span className="text-red-500">*</span>
                      </label>
                      <CustomSelect
                        id="billingCycle"
                        name="pricingModel.billingCycle"
                        selectedValue={values.pricingModel.billingCycle}
                        onChangeHandler={(value: string) =>
                          setFieldValue("pricingModel.billingCycle", value)
                        }>
                        {billingCycleValues.map((cycle) => (
                          <option key={cycle.value} value={cycle.value}>
                            {cycle.label}
                          </option>
                        ))}
                        <option value="custom">Custom</option>
                      </CustomSelect>
                      <ErrorMessage
                        name="pricingModel.billingCycle"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    {values.pricingModel.billingCycle === "custom" && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="customBillingInterval"
                            className="block text-sm font-medium leading-6 text-gray-900">
                            Interval <span className="text-red-500">*</span>
                          </label>
                          <Field
                            id="customBillingInterval"
                            name="pricingModel.customBillingInterval"
                            type="text"
                            placeholder="Enter interval"
                            className="input px-4 py-2 w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent mt-1"
                            pattern="\d*"
                            inputMode="numeric"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (/^\d*$/.test(value)) {
                                setFieldValue("pricingModel.customBillingInterval", value);
                              }
                            }}
                          />
                          <ErrorMessage
                            name="pricingModel.customBillingInterval"
                            component="p"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="customBillingUnit"
                            className="block text-sm font-medium leading-6 text-gray-900">
                            Unit <span className="text-red-500">*</span>
                          </label>
                          <CustomSelect
                            id="customBillingUnit"
                            name="pricingModel.customBillingUnit"
                            selectedValue={values.pricingModel.customBillingUnit}
                            onChangeHandler={(value: string) =>
                              setFieldValue("pricingModel.customBillingUnit", value)
                            }>
                            {customBillingUnitValues.map((unit) => (
                              <option key={unit.value} value={unit.value}>
                                {unit.label}
                              </option>
                            ))}
                          </CustomSelect>
                          <ErrorMessage
                            name="pricingModel.customBillingUnit"
                            component="p"
                            className="text-red-500 text-sm mt-1"
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="unitPrice"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Pricing <span className="text-red-500">*</span>
                        </label>
                        <Field
                          id="unitPrice"
                          name="pricingModel.unitPrice"
                          type="text"
                          placeholder="Enter pricing"
                          className="input px-4 py-2 w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent mt-1 disabled:bg-gray-100"
                          value={values.type === "paid" ? values.pricingModel.unitPrice : 0}
                          disabled={values.type === "free"}
                          pattern="\d*"
                          inputMode="numeric"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setFieldValue("pricingModel.unitPrice", value);
                              setFieldValue("pricingModel.unitPriceOriginal", value); // keep original in sync if not set
                            }
                          }}
                        />
                        <ErrorMessage
                          name="pricingModel.unitPrice"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="unit"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Currency <span className="text-red-500">*</span>
                        </label>
                        <CustomSelect
                          id="unit"
                          name="pricingModel.unit"
                          selectedValue={values.pricingModel.unit}
                          onChangeHandler={(value: string) =>
                            setFieldValue("pricingModel.unit", value)
                          }>
                          {unitValues.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </CustomSelect>
                        <ErrorMessage
                          name="pricingModel.unit"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="unitPriceOriginal"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Original Price (before discount)
                        </label>
                        <Field
                          id="unitPriceOriginal"
                          name="pricingModel.unitPriceOriginal"
                          type="text"
                          placeholder="Original price"
                          className="input px-4 py-2 w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent mt-1"
                          pattern="\d*"
                          inputMode="numeric"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setFieldValue("pricingModel.unitPriceOriginal", value);
                            }
                          }}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="discountPercent"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Discount (%)
                        </label>
                        <Field
                          id="discountPercent"
                          name="pricingModel.discountPercent"
                          type="text"
                          placeholder="e.g. 10 for 10%"
                          className="input px-4 py-2 w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent mt-1"
                          pattern="\d*"
                          inputMode="numeric"
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              setFieldValue("pricingModel.discountPercent", value);
                            }
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="trialPeriod"
                        className="block text-sm font-medium leading-6 text-gray-900">
                        Trial Period in Days (Optional)
                      </label>
                      <Field
                        id="trialPeriod"
                        name="trialPeriod"
                        type="text"
                        placeholder="Enter the trial period"
                        className="input px-4 py-2 w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent mt-1"
                        pattern="\d*"
                        inputMode="numeric"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            setFieldValue("trialPeriod", value);
                          }
                        }}
                      />
                      <ErrorMessage
                        name="trialPeriod"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-medium leading-6 text-gray-900">
                        About <span className="text-red-500">*</span>
                      </label>
                      <Field
                        id="description"
                        name="description"
                        as="textarea"
                        rows={15}
                        placeholder="Enter description"
                        className="input px-4 py-2 w-full rounded-lg border border-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-transparent mt-1"
                      />
                      <ErrorMessage
                        name="description"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-x-4 pt-4">
                  <button
                    type="button"
                    className="py-2 px-6 rounded-full border border-pink-300 text-sm font-medium font-space-grotesk transition-colors hover:bg-pink-50"
                    onClick={() => navigate(-1)}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="py-2 px-6 rounded-full bg-pink-300 text-sm font-medium font-space-grotesk transition-colors hover:bg-pink-400"
                    disabled={isSubmitting}>
                    {isSubmitting ? "Adding..." : "Add Plan"}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
